import styled from "@emotion/styled"
import { PIcon, PTag } from "@porsche-design-system/components-react"
import type { PTagProps } from "@porsche-design-system/components-react"
import { spacingFluidXs } from "@porsche-design-system/components-react/emotion"
import type { PageTeaserFieldView, PageTeaserTagView } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { CopyToClipboard } from "../copyToClipboard/CopyToClipboard"
import { Skeleton } from "../skeleton/Skeleton"
import { Tooltip } from "../tooltip/Tooltip"
import { isPageTeaserSkeletonMarker } from "./pageTeaserView.utils"

interface PageTeaserTagsProps {
	isWithinStickyHeader?: boolean
	view?: PageTeaserFieldView<PageTeaserTagView[]>
}

export const PageTeaserTags = ({ isWithinStickyHeader, view }: PageTeaserTagsProps) => {
	const defaultVariant: PTagProps["variant"] = useMemo(
		() => (isWithinStickyHeader ? undefined : "secondary"),
		[isWithinStickyHeader],
	)

	if (isPageTeaserSkeletonMarker(view)) {
		return (
			<Wrapper>
				<Skeleton.Tag />
				<Skeleton.Tag />
				<Skeleton.Tag />
			</Wrapper>
		)
	}

	if (!view || view.length === 0) {
		return null
	}

	return (
		<Wrapper>
			{view.map((item: PageTeaserTagView, index: number) => {
				const detailedItem = typeof item === "object" ? item : { label: item }
				const { color, copyable, label, testId, tooltip } = detailedItem
				const variant = color ?? defaultVariant

				if (copyable) {
					return (
						<PTag key={index} variant={variant} data-testid={testId}>
							<Tooltip content={tooltip} placement="bottom">
								<CopyToClipboard value={label} as="button" compact />
							</Tooltip>
							<PIcon name="copy" color="inherit" size="2xs" />
						</PTag>
					)
				}

				return (
					<Tooltip key={index} content={tooltip} placement="bottom">
						<PTag variant={variant} data-testid={testId}>
							{label}
						</PTag>
					</Tooltip>
				)
			})}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	display: flex;
	flex-wrap: wrap; /* prevents overflow and horizontal scrolling on mobile */
	gap: ${spacingFluidXs};
`
